/**
 * @file Authentication service.
 * @description Handles login/session persistence, auth state checks, and user session helpers.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { ApiService } from './apiService';
import { AuthResponse, LoginForm, User } from '../types';

/**
 * Authentication Service
 * Handles all authentication-related operations
 */
export class AuthService {
  private static parseJwtExp(token: string): number | null {
    try {
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) return null;
      const payload = JSON.parse(atob(payloadBase64));
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  static isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    const exp = this.parseJwtExp(token);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  }
  /**
   * Authenticate user with email and password
   */
  static async login(credentials: LoginForm): Promise<AuthResponse> {
    const response = await ApiService.login(credentials);
    return response as unknown as AuthResponse;
  }

  /**
   * Logout user and clear stored credentials
   */
  static logout(): void {
    ApiService.logout();
    window.location.href = '/login';
  }

  /**
   * Handle token expiration with user notification
   */
  static handleTokenExpiration(message?: string): void {
    ApiService.logout();
    
    // Show notification
    const notificationMessage = message || 'Your session has expired. Please log in again.';
    
    // Create a simple notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-sm';
    notification.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Session Expired</h3>
          <div class="mt-2 text-sm text-red-700">
            <p>${notificationMessage}</p>
            <p class="mt-1">Redirecting to login...</p>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto-remove notification and redirect
    setTimeout(() => {
      notification.remove();
      window.location.href = '/login';
    }, 3000);
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = localStorage.getItem('jiggle_token');
    if (!token) return false;

    if (this.isTokenExpired(token)) {
      ApiService.logout();
      return false;
    }

    return true;
  }

  /**
   * Get current user token
   */
  static getToken(): string | null {
    return localStorage.getItem('jiggle_token');
  }

  /**
   * Get current user data
   */
  static getUser(): User | null {
    const userData = localStorage.getItem('jiggle_user');
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Set user data in localStorage
   */
  static setUser(user: User): void {
    localStorage.setItem('jiggle_user', JSON.stringify(user));
  }
}
